import React, { createContext, useState, useContext, useMemo } from 'react';

const HighlightedEvent = createContext();

/**
 * Evento em destaque compartilhado entre a listagem e o mapa.
 *
 * Lista e mapa são irmãos sob a página principal e não tinham nenhum vínculo:
 * passar o mouse num card não dizia onde ele fica no mapa, e passar num pin não
 * dizia a qual card ele corresponde. Guardar só o slug aqui deixa os dois lados
 * reagirem sem que um precise conhecer o outro.
 *
 * `source` registra de onde veio o destaque ('list' ou 'map'), para que o lado
 * que originou a interação não tente rolar/centralizar a si mesmo.
 */
export default function HighlightedEventProvider({ children }) {
    const [highlighted, setHighlighted] = useState({ slug: null, source: null });

    const value = useMemo(() => ({
        highlightedSlug: highlighted.slug,
        highlightedSource: highlighted.source,
        highlightEvent: (slug, source) => setHighlighted({ slug, source }),
        clearHighlight: () => setHighlighted({ slug: null, source: null }),
    }), [highlighted]);

    return (
        <HighlightedEvent.Provider value={value}>
            {children}
        </HighlightedEvent.Provider>
    );
}

export function useHighlightedEvent() {
    const context = useContext(HighlightedEvent);

    if (!context) throw new Error('useHighlightedEvent must be used within a HighlightedEventProvider');

    return context;
}

/**
 * Versão tolerante para componentes que também renderizam fora da página
 * principal (o card de evento aparece em carrosséis e listas sem o provider).
 */
export function useHighlightedEventOptional() {
    return useContext(HighlightedEvent) ?? {
        highlightedSlug: null,
        highlightedSource: null,
        highlightEvent: () => {},
        clearHighlight: () => {},
    };
}
